import React from 'react';
import isFunction from 'lodash/isFunction';

import { findFirstTabbableElement, findLastTabbableElement } from '@Utils/DOM';

const instances = [];

/* eslint-disable react/prop-types */
export class KeepFocusWrapper extends React.PureComponent {
  constructor(props) {
    super(props);

    this.ref = React.createRef();
    this._lastFocus = null;
    this._originalFocus = null;
    instances.push(this);
  }

  componentDidMount() {
    const { focusOnMount, needKeepFocus } = this.props;

    document.addEventListener('focus', this.handleFocus, true);

    // store original focus
    if (needKeepFocus) {
      this._originalFocus = document.activeElement;
    }

    if (focusOnMount) {
      this.ref.current.focus();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('focus', this.handleFocus, true);
    const index = instances.findIndex((instance) => instance === this);

    // restore original focus
    if (this._originalFocus) {
      if (isFunction(this._originalFocus.focus)) this._originalFocus.focus();
      this._originalFocus = null;
    }

    instances.splice(index, 1);
  }

  setFocusInsideComponentDomNode(e) {
    const { current } = this.ref;
    const isFocusInside = current.contains(e.target);

    if (isFocusInside) {
      // focus inside modal - normal flow
      this._lastFocus = e.target;
      return;
    }

    // focus gets outside of modal
    const first = findFirstTabbableElement(current);
    const last = findLastTabbableElement(current);

    if (e.target === document) {
      // when window gets focus - return to previous focus
      if (this._lastFocus) this._lastFocus.focus();
    } else if (this._lastFocus === first) {
      // when on first tabbable element - go to last
      last.focus();
    } else if (this._lastFocus === last) {
      // when on last tabbable element - go to first
      first.focus();
    } else {
      current.focus();
    }
  }

  handleFocus = (e) => {
    if (instances[instances.length - 1] !== this) {
      return;
    }

    const { needKeepFocus } = this.props;

    if (!needKeepFocus) {
      return;
    }

    this.setFocusInsideComponentDomNode(e);
  };

  render() {
    const { children } = this.props;

    if (isFunction(children)) {
      return (
        <div data-testid="keep-focus-wrapper" tabIndex="-1" ref={this.ref}>
          {children(this.props)}
        </div>
      );
    }

    return (
      <div data-testid="keep-focus-wrapper" tabIndex="-1" ref={this.ref}>
        {children}
      </div>
    );
  }
}

KeepFocusWrapper.defaultProps = {
  needKeepFocus: false,
  focusOnMount: false,
};

export function KeepFocusHoc(config = {}) {
  const { needKeepFocus, focusOnMount } = config;

  return function KeepFocusProvider(Component) {
    return function KeepFocusWrapperHoc(props) {
      const { children, ...otherProps } = props;
      const nextNeedKeepFocus = isFunction(needKeepFocus) ? needKeepFocus(props) : Boolean(needKeepFocus);

      return (
        <KeepFocusWrapper {...otherProps} {...config} needKeepFocus={nextNeedKeepFocus} focusOnMount={focusOnMount}>
          <Component {...otherProps}>{children}</Component>
        </KeepFocusWrapper>
      );
    };
  };
}
